import { useGetStudentBannerDetails } from '@actions';
import { ReactComponent as AvatarIcon } from '@assets/icons/V4/default-profile-pic.svg';
import { ReactComponent as MarkedIcon } from '@assets/icons/V4/parent-page/marked-icon.svg';
import { ReactComponent as MissedSessionsIcon } from '@assets/icons/V4/parent-page/missed-sessions-icon.svg';
import { ReactComponent as RemainingSessionsIcon } from '@assets/icons/V4/parent-page/remaining-sessions-icon.svg';
import { ReactComponent as RescheduledSessionsIcon } from '@assets/icons/V4/parent-page/rescheduled-sessions-icon.svg';
import SkeletonBox from '@components/V4/SkeletonBox';
import { useTranslation } from 'apps/agora/src/TranslationContext';

import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { useParams } from 'react-router';

const ParentPageHeader = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const token = localStorage.getItem('parentToken');
  const { data, isLoading } = useGetStudentBannerDetails({ userId: studentId, token: token ?? '' });
  const { t, language } = useTranslation();

  const strategist = data?.educationStrategist;
  const strategistName =
    strategist?.fullName ?? `${strategist?.firstName ?? ''} ${strategist?.lastName ?? ''}`.trim();

  const stats = [
    {
      label: t('parent_completed_sessions'),
      value: data?.sessions.completed,
      icon: <MarkedIcon />,
      accentColor: 'text-customLime',
    },
    {
      label: t('parent_remaining_sessions'),
      value: data?.sessions.remaining,
      icon: <RemainingSessionsIcon />,
      accentColor: 'text-customGrey',
    },
    {
      label: t('parent_rescheduled_sessions'),
      value: data?.sessions.rescheduled,
      icon: <RescheduledSessionsIcon />,
      accentColor: 'text-customGold',
    },
    {
      label: t('parent_missed_sessions'),
      value: data?.sessions.missed,
      icon: <MissedSessionsIcon />,
      accentColor: 'text-customRed',
    },
  ];

  return isLoading ? (
    <SkeletonBox className="w-full h-80"></SkeletonBox>
  ) : (
    <section className="flex flex-col gap-6 p-6 rounded-xl bg-surfaceObject laptop:flex-row laptop:justify-between laptop:items-center laptop:py-8">
      <div className="flex flex-col gap-6 tablet:flex-row tablet:items-center">
        {/* avatar */}
        <div className="">
          {data?.avatar ? (
            <img
              src={data?.avatar}
              alt="student avatar"
              crossOrigin="anonymous"
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <AvatarIcon className="w-16 h-16" />
          )}
        </div>

        {/* Details */}
        <div className="flex flex-col gap-2">
          <h2 className="font-raleway font-semibold text-xl">
            {data?.name}
            {language === 'en' && "'s"} {t('parent_overview')}
          </h2>
          <p className="flex flex-col gap-0.5 text-xs text-customGrey laptop:hidden">
            <span>
              {t('parent_started')} {data?.createdAt}
            </span>
            <span>
              {data?.course.completed} {t('parent_courses_completed')}
            </span>
            <span>
              {data?.course.inProgress} {t('parent_courses_in_progress')}
            </span>
            {!!data?.course.notStarted && (
              <span>
                {data?.course.notStarted} {t('parent_courses_not_started')}
              </span>
            )}
            <span>
              {t('parent_graduation_year')}: {data?.graduationYear}
            </span>
          </p>

          <p className="hidden flex-col gap-0.5 text-xs text-customGrey laptop:flex laptop:gap-1">
            <span>
              {t('parent_started')} {data?.createdAt}
            </span>
            <span>
              <span>
                {data?.course.completed} {t('parent_courses_completed')} •{' '}
              </span>
              <span>
                {data?.course.inProgress} {t('parent_courses_in_progress')}{' '}
                {!!data?.course.notStarted && <>•</>}{' '}
              </span>
              {!!data?.course.notStarted && (
                <span>
                  {data?.course.notStarted} {t('parent_courses_not_started')}
                </span>
              )}
            </span>
            <span>
              {t('parent_graduation_year')}: {data?.graduationYear}
            </span>
          </p>

          {data?.whatsappGroupLink && (
            <a
              href={data.whatsappGroupLink}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex w-max items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2 font-raleway font-bold text-white hover:opacity-90"
            >
              {t('parent_join_whatsapp')}
            </a>
          )}

          {strategist && (
            <div className="mt-1 flex flex-col gap-0.5 text-xs text-customGrey">
              <span className="font-semibold text-white">{t('education_strategist')}</span>
              {strategistName && <span>{strategistName}</span>}
              {strategist.phoneNumber?.number && (
                <a
                  className="hover:text-customLila"
                  href={`tel:${strategist.phoneNumber.countryCode ?? ''}${
                    strategist.phoneNumber.number
                  }`}
                >
                  {`${strategist.phoneNumber.countryCode ?? ''}${strategist.phoneNumber.number}`}
                </a>
              )}
              {strategist.email && (
                <a className="hover:text-customLila" href={`mailto:${strategist.email}`}>
                  {strategist.email}
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="flex justify-between tablet:gap-6 tablet:justify-start">
        {stats.map((stat, index) => (
          <div key={`${index}-${stat.label}`} className="">
            <div className="flex items-center justify-center gap-1">
              {stat.icon}
              <p
                className={mergeClassNames(
                  'font-raleway font-semibold text-3.5xl',
                  stat.accentColor
                )}
              >
                {stat.value}
              </p>
            </div>
            <p className="font-raleway font-semibold text-customGrey text-xxs text-center tablet:w-20">
              {stat.label}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ParentPageHeader;
